import { rendererCapacity } from "./renderer-capacity";
const { maxNodes, maxConnections } = rendererCapacity;
export type RenderablePacket = {
  activationWeights?: Float32Array;
  nodes: ArrayLike<number>;
  connections: ArrayLike<number>;
  inputIndices: ArrayLike<number>;
  outputIndices: ArrayLike<number>;
};

export type PackedBatchResult = { packets: RenderablePacket[] };

function toPackedBytes(payload: unknown): Uint8Array {
  if (payload instanceof Uint8Array) return payload;
  if (payload instanceof ArrayBuffer) return new Uint8Array(payload);
  if (typeof payload === "string") {
    const binary = atob(payload); const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
    return bytes;
  }
  if (Array.isArray(payload)) return Uint8Array.from(payload);
  throw new Error("Packed CPPN payload was not received as bytes.");
}
function readPackedUint32(view: DataView, offset: number): number { return view.getUint32(offset, true); }
function packedFloatView(bytes: Uint8Array, offset: number, count: number): Float32Array {
  const absoluteOffset = bytes.byteOffset + offset;
  if (absoluteOffset % 4 === 0) return new Float32Array(bytes.buffer, absoluteOffset, count);
  return new Float32Array(bytes.slice(offset, offset + count * 4).buffer);
}
function packedIntView(bytes: Uint8Array, offset: number, count: number): Int32Array {
  const absoluteOffset = bytes.byteOffset + offset;
  if (absoluteOffset % 4 === 0) return new Int32Array(bytes.buffer, absoluteOffset, count);
  return new Int32Array(bytes.slice(offset, offset + count * 4).buffer);
}
export function parsePackedBatch(payload: unknown): PackedBatchResult {
  const bytes = toPackedBytes(payload);
  if (bytes.byteLength < 20) throw new Error("Packed CPPN payload is truncated.");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const version = readPackedUint32(view, 4);
  const recordBytes = version === 3 ? 32 : version === 2 ? 28 : 24;
  if (readPackedUint32(view, 0) !== 0x31435050 || (version !== 1 && version !== 2 && version !== 3))
    throw new Error("Packed CPPN payload header is not recognized.");
  const candidateCount = readPackedUint32(view, 8);
  const directoryOffset = readPackedUint32(view, 12);
  if (readPackedUint32(view, 16) !== bytes.byteLength || directoryOffset + candidateCount * recordBytes > bytes.byteLength)
    throw new Error("Packed CPPN payload directory is invalid.");

  const packets: RenderablePacket[] = [];
  for (let index = 0; index < candidateCount; index++) {
    const entry = directoryOffset + index * recordBytes;
    const nodeCount = readPackedUint32(view, entry);
    const connectionCount = readPackedUint32(view, entry + 4);
    const nodeOffset = readPackedUint32(view, entry + 8);
    const connectionOffset = readPackedUint32(view, entry + 12);
    const inputOffset = readPackedUint32(view, entry + 16);
    const outputOffset = readPackedUint32(view, entry + 20);
    const outputCount = version >= 2 ? readPackedUint32(view, entry + 24) : 3;
    const activationOffset = version === 3 ? readPackedUint32(view, entry + 28) : 0;
    if ((outputCount !== 3 && outputCount !== 8) || nodeCount > maxNodes || connectionCount > maxConnections
      || (version === 3 && activationOffset + nodeCount * 32 > bytes.byteLength)
      || nodeOffset + nodeCount * 16 > bytes.byteLength
      || connectionOffset + connectionCount * 16 > bytes.byteLength
      || inputOffset + 24 > bytes.byteLength || outputOffset + outputCount * 4 > bytes.byteLength)
      throw new Error("Packed CPPN payload record exceeds the current WebGL2 renderer capacity.");
    packets.push({
      activationWeights: version === 3 ? packedFloatView(bytes, activationOffset, nodeCount * 8) : undefined,
      nodes: packedFloatView(bytes, nodeOffset, nodeCount * 4),
      connections: packedFloatView(bytes, connectionOffset, connectionCount * 4),
      inputIndices: packedIntView(bytes, inputOffset, 6),
      outputIndices: packedIntView(bytes, outputOffset, outputCount)
    });
  }
  return { packets };
}
